import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPen, faPlay, faPause, faBan, faRotateLeft, faInbox, faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { Survey, SurveyStatus } from '../../types';
import { API_BASE_URL } from '../../config/api';
import {
  SURVEY_STATUS_OPTIONS,
  excerpt,
  sortSurveys,
  surveySettingLines,
  surveyStatusMeta,
} from './surveyUtils';

const SurveysListPage: React.FC = () => {
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);

  const fetchSurveys = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = { limit: 100, page: 1 };
      if (statusFilter) params.status = statusFilter;
      const response = await axios.get(`${API_BASE_URL}/surveys`, {
        withCredentials: true,
        params,
      });
      setSurveys(sortSurveys(response.data.surveys || []));
      setError(null);
    } catch (err: any) {
      console.error('Error fetching surveys:', err);
      setError(err.response?.data?.message || 'Failed to load surveys');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchSurveys();
  }, [fetchSurveys]);

  const handleStatusChange = async (survey: Survey, status: SurveyStatus) => {
    setUpdatingStatusId(survey._id);
    setError(null);
    setSurveys((prev) =>
      sortSurveys(prev.map((entry) => (entry._id === survey._id ? { ...entry, status } : entry)))
    );

    try {
      await axios.patch(
        `${API_BASE_URL}/surveys/${survey._id}`,
        { status },
        { withCredentials: true }
      );
    } catch (err: any) {
      console.error('Error updating survey status:', err);
      setError(err.response?.data?.message || 'Failed to update survey status');
      fetchSurveys();
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const formatDate = (value?: string | Date) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div className="input-group input-group-sm" style={{ width: 'auto' }}>
          <span className="input-group-text">Status</span>
          <select
            className="form-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label="Filter surveys by status"
          >
            <option value="">All</option>
            {SURVEY_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        <Link to="/surveys/new" className="btn btn-primary btn-sm">
          <FontAwesomeIcon icon={faPlus} className="me-1" />
          New Survey
        </Link>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)} aria-label="Close" />
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : surveys.length > 0 ? (
        <div className="deploy-test-list">
          {surveys.map((survey) => {
            const meta = surveyStatusMeta(survey.status);
            const questionCount = survey.questionCount ?? survey.questions?.length ?? 0;
            const summary = excerpt(survey.summary);
            const settingLines = surveySettingLines(survey);
            const resultCount = survey.resultCount || 0;
            const optOutCount = survey.optOutCount || 0;
            return (
              <div key={survey._id} className="deploy-test-card survey-card">
                <div className={`deploy-test-card-status ${survey.status}`}>
                  {meta.label}
                </div>
                <div className="deploy-test-card-body">
                  <div className="deploy-test-card-main min-w-0">
                    <h3 className="deploy-test-card-title">{survey.name}</h3>
                    <div className="deploy-test-card-date">Active Since: {formatDate(survey.createdAt)}</div>
                    {summary && (
                      <div className="mt-2">
                        <span className="deploy-test-card-version-label">Summary</span>
                        <div className="text-muted">{summary}</div>
                      </div>
                    )}
                    <div className="deploy-test-card-versions mt-3">
                      <div>
                        <span className="deploy-test-card-version-label">Who</span>
                        <span className="deploy-test-card-version-value is-multiline">
                          {settingLines.who.map((line) => (
                            <span key={line}>{line}</span>
                          ))}
                        </span>
                      </div>
                      <div>
                        <span className="deploy-test-card-version-label">Devices</span>
                        <span className="deploy-test-card-version-value is-multiline">
                          {settingLines.devices.map((line) => (
                            <span key={line}>{line}</span>
                          ))}
                        </span>
                      </div>
                      <div>
                        <span className="deploy-test-card-version-label">Platform</span>
                        <span className="deploy-test-card-version-value is-multiline">
                          {settingLines.platforms.map((line) => (
                            <span key={line}>{line}</span>
                          ))}
                        </span>
                      </div>
                      <div>
                        <span className="deploy-test-card-version-label">When</span>
                        <span className="deploy-test-card-version-value is-multiline">
                          {settingLines.when.map((line) => (
                            <span key={line}>{line}</span>
                          ))}
                        </span>
                      </div>
                      <div>
                        <span className="deploy-test-card-version-label">How Many</span>
                        <span className="deploy-test-card-version-value is-multiline">
                          {settingLines.howMany.map((line) => (
                            <span key={line}>{line}</span>
                          ))}
                        </span>
                      </div>
                      <div>
                        <span className="deploy-test-card-version-label">Rollout</span>
                        <span className="deploy-test-card-version-value is-multiline">
                          {settingLines.rollout.map((line) => (
                            <span key={line}>{line}</span>
                          ))}
                        </span>
                      </div>
                    </div>
                    <div className="survey-results-box">
                      <div className="survey-results-box-head">
                        <span className="survey-results-box-title">
                          <FontAwesomeIcon icon={faInbox} />
                          Results
                        </span>
                        <Link
                          to={`/surveys/${survey._id}/results`}
                          className="btn btn-primary survey-results-button"
                        >
                          See Results
                          <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="ms-2" />
                        </Link>
                      </div>
                      <div className="survey-results-box-stats">
                        <div>
                          <span className="deploy-test-card-version-label">Questions</span>
                          <span className="deploy-test-card-version-value">{questionCount}</span>
                        </div>
                        <div>
                          <span className="deploy-test-card-version-label">Results</span>
                          <span className="deploy-test-card-version-value">{resultCount}</span>
                        </div>
                        <div>
                          <span className="deploy-test-card-version-label">Opt Outs</span>
                          <span className="deploy-test-card-version-value">{optOutCount}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="survey-card-rail">
                    <div className="survey-card-rail-actions">
                      <div className="survey-card-status-actions">
                        {(survey.status === 'planning' || survey.status === 'paused') && (
                          <button
                            type="button"
                            className="btn btn-success survey-card-status-action"
                            onClick={() => handleStatusChange(survey, 'active')}
                            disabled={updatingStatusId === survey._id}
                          >
                            <FontAwesomeIcon icon={faPlay} className="me-1" />
                            Start
                          </button>
                        )}
                        {survey.status === 'active' && (
                          <button
                            type="button"
                            className="btn btn-warning survey-card-status-action"
                            onClick={() => handleStatusChange(survey, 'paused')}
                            disabled={updatingStatusId === survey._id}
                          >
                            <FontAwesomeIcon icon={faPause} className="me-1" />
                            Pause
                          </button>
                        )}
                        {survey.status !== 'closed' && (
                          <button
                            type="button"
                            className="btn btn-outline-danger survey-card-status-action"
                            onClick={() => handleStatusChange(survey, 'closed')}
                            disabled={updatingStatusId === survey._id}
                          >
                            <FontAwesomeIcon icon={faBan} className="me-1" />
                            Close
                          </button>
                        )}
                        {survey.status === 'closed' && (
                          <button
                            type="button"
                            className="btn btn-outline-primary survey-card-status-action"
                            onClick={() => handleStatusChange(survey, 'active')}
                            disabled={updatingStatusId === survey._id}
                          >
                            <FontAwesomeIcon icon={faRotateLeft} className="me-1" />
                            Reopen
                          </button>
                        )}
                      </div>
                      <Link
                        to={`/surveys/${survey._id}`}
                        className="btn btn-outline-primary btn-sm survey-card-edit-action"
                        title="Edit survey"
                      >
                        <FontAwesomeIcon icon={faPen} className="me-1" />
                        Edit
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="deploy-test-card-empty">No surveys yet</div>
      )}
    </>
  );
};

export default SurveysListPage;
